import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  Box,
  Button,
  Chip,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { ChatUser } from '../../types/chat';
import { chatAPI } from '../../services/api';
import ChatAvatar from './ChatAvatar';

interface GroupConversationDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateGroup: (groupName: string, participantIds: number[]) => void;
  currentUserId: number;
}

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

const GroupConversationDialog: React.FC<GroupConversationDialogProps> = ({
  open,
  onClose,
  onCreateGroup,
  currentUserId,
}) => {
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ChatUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<ChatUser[]>([]);
  const [search, setSearch] = useState('');
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      loadUsers();
      // Reset state when opening
      setSelectedUsers([]);
      setGroupName('');
      setSearch('');
    }
  }, [open]);

  useEffect(() => {
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      setFilteredUsers(
        users.filter((user) =>
          user.full_name.toLowerCase().includes(searchLower)
        )
      );
    } else {
      setFilteredUsers(users);
    }
  }, [search, users]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await chatAPI.getChatUsers();
      // Filter out current user
      const otherUsers = response.filter((u) => u.id !== currentUserId);
      setUsers(otherUsers);
      setFilteredUsers(otherUsers);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUser = (user: ChatUser) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.find((u) => u.id === user.id);
      if (isSelected) {
        return prev.filter((u) => u.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const handleRemoveUser = (userId: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedUsers.length === 0) return;

    setCreating(true);
    try {
      onCreateGroup(groupName.trim(), selectedUsers.map((u) => u.id));
      onClose();
    } finally {
      setCreating(false);
    }
  };

  const isUserSelected = (userId: number) => {
    return selectedUsers.some((u) => u.id === userId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '80vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon sx={{ color: OUTLOOK_BLUE }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: OUTLOOK_BLUE }}>
            Nouveau Groupe
          </Typography>
        </Box>
        <Box
          onClick={onClose}
          sx={{
            cursor: 'pointer',
            color: '#666',
            '&:hover': { color: '#333' },
          }}
        >
          <CloseIcon />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Group name input */}
        <TextField
          fullWidth
          size="small"
          label="Nom du groupe"
          placeholder="Entrez le nom du groupe..."
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused fieldset': {
                borderColor: OUTLOOK_BLUE,
              },
            },
          }}
        />

        {/* Selected users chips */}
        {selectedUsers.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedUsers.map((user) => (
              <Chip
                key={user.id}
                label={user.full_name}
                size="small"
                onDelete={() => handleRemoveUser(user.id)}
                sx={{
                  backgroundColor: OUTLOOK_BLUE,
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': { color: 'white' },
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* Search input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Rechercher des utilisateurs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#999' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused fieldset': {
                borderColor: OUTLOOK_BLUE,
              },
            },
          }}
        />

        {/* User list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: OUTLOOK_BLUE }} />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: '#666', textAlign: 'center', py: 4 }}
          >
            {search ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur disponible'}
          </Typography>
        ) : (
          <List sx={{ maxHeight: 250, overflowY: 'auto', mx: -2 }}>
            {filteredUsers.map((user) => (
              <ListItem key={user.id} disablePadding>
                <ListItemButton
                  onClick={() => handleToggleUser(user)}
                  sx={{
                    borderRadius: 1,
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                >
                  <Checkbox
                    checked={isUserSelected(user.id)}
                    sx={{
                      color: '#999',
                      '&.Mui-checked': { color: OUTLOOK_BLUE },
                    }}
                  />
                  <ListItemAvatar>
                    <ChatAvatar
                      name={user.full_name}
                      isOnline={user.is_online}
                      lastSeenAt={user.last_seen_at}
                      size={40}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.full_name}
                    secondary={user.is_online ? 'En ligne' : 'Hors ligne'}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{
                      sx: { color: user.is_online ? '#4caf50' : '#999' },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateGroup}
          disabled={!groupName.trim() || selectedUsers.length === 0 || creating}
          sx={{
            backgroundColor: OUTLOOK_BLUE,
            '&:hover': { backgroundColor: '#005a9e' },
          }}
        >
          {creating ? <CircularProgress size={20} color="inherit" /> : 'Créer le groupe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupConversationDialog;
